import { Component, OnInit, ChangeDetectorRef, Output, EventEmitter, HostListener } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { SharedModule } from '@shared/shared_module';
import { ApiService, SignupRequest, Region, UserRole, Country } from '@core/services/api/api.service';
import { TableComponent, TableColumn, FilterConfigType } from '@shared/components/table/table.component';
import { AcceptRequestPopupComponent } from './accept-request-popup/accept-request-popup.component';
import { DeclineRequestPopupComponent } from './decline-request-popup/decline-request-popup.component';
import { HttpContext, HttpErrorResponse } from '@angular/common/http';
import { DialogModule } from 'primeng/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { CertificateCellComponent } from './certificate-cell/certificate-cell/certificate-cell.component';
import { SHOW_LOADER } from '@shared/components/loader/interceptors/loader.interceptor';

@Component({
  selector: 'app-user-requests',
  standalone: true,
  imports: [
    CommonModule,
    TableComponent,
    SharedModule,
    AcceptRequestPopupComponent,
    DeclineRequestPopupComponent,
    DialogModule,
    NgxExtendedPdfViewerModule
  ],
  templateUrl: './user-requests.component.html',
  styleUrls: ['./user-requests.component.scss'],
  providers: [DatePipe]
})
export class UserRequestsComponent implements OnInit {
  requestData: SignupRequest[] = [];
  requestColumns: TableColumn[] = [];
  requestGlobalFilterFields: string[] = [];
  requestFiltersConfig: FilterConfigType[] = [];
  requestFiltersValue: { [key: string]: any } = {};

  requestActions: any[] = [];

  requestTotalRecords: number = 0;
  requestLoading: boolean = false;
  requestPage: number = 1;
  requestPageSize: number = 10;
  requestLastLazyLoadEvent: LazyLoadEvent | null = null;

  filterRegions: Region[] = [];
  filterUserRoles: UserRole[] = [];
  filterCountries: Country[] = [];

  showGenericSuccessDialog: boolean = false;
  showDeclineRequestDialog: boolean = false;
  selectedRequestForDecline: SignupRequest | null = null;

  @Output() totalRequestsChanged = new EventEmitter<number>();

  // Properties for PDF viewer
  displayPdfViewer: boolean = false;
  pdfUrl: string | null = null;
  pdfFileName: string = 'Training Certificate';
  safePdfUrl: any = null; // SafeResourceUrl for iframe

  // Base columns without actions
  baseColumns: TableColumn[] = [];

  // Click popup state for +N
  listPopupVisible: boolean = false;
  listPopupTitle: string = 'Items';
  listPopupItems: string[] = [];
  listPopupX: number = 0;
  listPopupY: number = 0;

  private docClickCapture?: (e: MouseEvent) => void;

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly apiService: ApiService,
    private readonly datePipe: DatePipe,
    private readonly messageService: MessageService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    this.fetchFilterOptions();
    this.setRequestsTabConfig();
    this.initializeFilterValues(this.requestFiltersConfig, this.requestFiltersValue);
    
    // Set initial column state based on default status
    this.updateColumnsBasedOnStatus(this.requestFiltersValue['statuses']);
    
    this.loadSignupRequests({ first: 0, rows: this.requestPageSize });
  }

  ngOnDestroy(): void {
    if (this.docClickCapture) {
      document.removeEventListener('click', this.docClickCapture, true);
      this.docClickCapture = undefined;
    }
  }

  initializeFilterValues(config: FilterConfigType[], values: { [key: string]: any }) {
    config.forEach(f => {
      if (f.type === 'multiselect') {
        values[f.key] = [];
      } else if (f.type === 'daterange') {
        values[f.key] = { from: null, to: null };
      } else {
        values[f.key] = null;
      }
    });

    values['statuses'] = 'pending';
  }

  onLazyLoadEventFromTable(event: LazyLoadEvent): void {
    this.requestLastLazyLoadEvent = event;
    this.loadSignupRequests(event);
  }

  onRequestsSearchClicked(filters: { [key: string]: any }) {
    this.requestFiltersValue = { ...filters };
    
    // Ensure status is set to pending if not defined in filters
    if (!this.requestFiltersValue['statuses']) {
      this.requestFiltersValue['statuses'] = 'pending';
    }

    // Update columns based on selected status
    this.updateColumnsBasedOnStatus(this.requestFiltersValue['statuses']);

    const currentGlobalFilter = this.requestLastLazyLoadEvent?.globalFilter;
    this.requestLastLazyLoadEvent = {
      first: 0,
      rows: this.requestPageSize,
      globalFilter: currentGlobalFilter
    };
    this.loadSignupRequests(this.requestLastLazyLoadEvent);
  }

  updateColumnsBasedOnStatus(status: string): void {
    // Only add the actions column if status is pending
    if (status === 'pending') {
      this.requestColumns = [
        ...this.baseColumns,
        { field: "actions", header: "Actions", sortable: false, isAction: true }
      ];
    } else {
      // For rejected status, don't include the actions column
      this.requestColumns = [...this.baseColumns];
    }
    
    this.cdr.detectChanges();
  }

  fetchFilterOptions(): void {
    this.apiService.getRegions().subscribe({
      next: (response) => {
        this.filterRegions = response.regions;
        this.updateRequestFilterOptions();
      },
      error: (err) => console.error('Failed to load filter regions:', err)
    });
    this.apiService.getUserRoles().subscribe({
      next: (response) => {
        this.filterUserRoles = response.user_roles;
        this.updateRequestFilterOptions();
      },
      error: (err) => console.error('Failed to load filter user roles:', err)
    });
    this.apiService.getCountries().subscribe({
      next: (response) => {
        if (response?.countries) {
          this.filterCountries = response.countries;
          this.updateRequestFilterOptions();
        }
      },
      error: (err) => console.error('Failed to load filter countries:', err)
    });
  }

  private updateRequestFilterOptions(): void {
    this.requestFiltersConfig = [
      {
        key: "region_id",
        label: "Region",
        type: "multiselect",
        options: this.filterRegions.map(r => ({ label: r.name, value: String(r.id) })),
      },
      {
        key: "country_id",
        label: "Country",
        type: "multiselect",
        options: this.filterCountries.map(c => ({ label: c.name, value: String(c.id) })),
      },
      {
        key: "role_id",
        label: "Role Type",
        type: "multiselect",
        options: this.filterUserRoles.map(r => ({ label: r.name, value: String(r.id) })),
      },
      {
        key: "statuses",
        label: "Status",
        type: "dropdown", 
        options: [
          { label: "Pending", value: "pending" },
          { label: "Rejected", value: "rejected" },
        ],
      },
      {
        key: "requested_at",
        label: "Date Range",
        type: "daterange",
      },
    ];
    this.cdr.detectChanges();
  }

  setRequestsTabConfig(): void {
    // Define base columns without actions
    this.baseColumns = [
      { field: "s_no", header: "S.No", sortable: false },
      { field: "first_name", header: "First Name", sortable: false },
      { field: "last_name", header: "Last Name", sortable: false },
      { field: "email", header: "Email Id", sortable: false },
      { field: "role_name", header: "Role Type", sortable: false, truncateAt:20 },
      { field: "country_name", header: "Country", sortable: false },
      { field: "region_name", header: "User Responsible Region", sortable: false },
      {
        field: "user_responsible_country_name",
        header: "User Responsible Country",
        sortable: false,
        isCustom: true,
        customTemplate: (rowData: any) => {
          const first = rowData.resp_first || 'N/A';
          const remain = Number(rowData.resp_remaining || 0);
          const others: string[] = Array.isArray(rowData.resp_remaining_names) ? rowData.resp_remaining_names : [];
          const encoded = encodeURIComponent(JSON.stringify(others));
             return  `  <div class="cell-multi">
            <span class="primary-text">${this.escapeHtml(String(first))}</span>
            ${remain ? `<span role="button" tabindex="0" class="more-badge white-outline country-count-badge--outlined">+${remain}</span><span class="more-items" style="display:none">${JSON.stringify(
                  others
                )}</span>` : ''}
          </div>`;
        }
      },
      { field: "company_name", header: "Company Name", sortable: false },
      { field: "formatted_requested_at", header: "Start Date", sortable: false },
      {
        field: "certificate_meta",
        header: "Certificate",
        sortable: false,
        customCellComponent: CertificateCellComponent
      }
    ];
    
    // Initially set columns with actions (assuming default is pending)
    this.requestColumns = [
      ...this.baseColumns,
      { field: "actions", header: "Actions", sortable: false, isAction: true }
    ];

    this.requestGlobalFilterFields = [
      "first_name", "last_name", "email", "company_name", "status"
    ];

    this.requestActions = [
      {
        label: "Accept",
        class: "accept-button",
        onClick: (row: SignupRequest) => this.acceptRequest(row),
        visible: (row: SignupRequest) => row.status === 'pending',
      },
      {
        label: "Decline",
        class: "decline-button",
        onClick: (row: SignupRequest) => this.declineRequest(row),
        visible: (row: SignupRequest) => row.status === 'pending',
      },
    ];
  }

  getRoleName(roleId: number): string {
    const role = this.filterUserRoles.find(r => r.id === roleId);
    return role ? role.name : 'N/A';
  }

  getCountryName(countryId: number): string {
    const country = this.filterCountries.find(c => c.id === countryId);
    return country ? country.name : 'N/A';
  }

  getRegionName(regionId: number): string {
    const region = this.filterRegions.find(r => r.id === regionId);
    return region ? region.name : 'N/A';
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return this.datePipe.transform(dateString, 'dd/MM/yyyy') || 'N/A';
  }

  private buildCertificateMeta(certificateInfo: any, fallbackUrl: string | null | undefined) {
    const info = certificateInfo ?? {};
    const url = info?.url || fallbackUrl || null;

    if (!url && !info?.status) {
      return null;
    }

    return {
      ...info,
      url,
      file_name: info?.file_name || (url ? this.extractFileNameFromUrl(url) : null),
    };
  }

  private extractFileNameFromUrl(url: string): string {
    if (!url) {
      return 'certificate.pdf';
    }
    return url.substring(url.lastIndexOf('/') + 1).split('?')[0] || 'certificate.pdf';
  }

  loadSignupRequests(event?: LazyLoadEvent): void {
    this.requestLoading = true;

    const currentEvent = event || this.requestLastLazyLoadEvent || { first: 0, rows: this.requestPageSize };

    this.requestPage = (currentEvent.first ?? 0) / (currentEvent.rows ?? this.requestPageSize) + 1;
    this.requestPageSize = currentEvent.rows ?? this.requestPageSize;

    const filtersToSend: { [key: string]: any } = {};

    for (const key in this.requestFiltersValue) {
      if (Object.hasOwn(this.requestFiltersValue, key)) {
        let value = this.requestFiltersValue[key];

        const filterConfig = this.requestFiltersConfig.find(f => f.key === key);

        if (filterConfig) {
          // Handle multiselect: value is an array of objects
          if (filterConfig.type === 'multiselect' && Array.isArray(value)) {
            value = value.map(item => item.value).filter(v => v !== null && v !== undefined);
          }
          // Handle dropdown: value is a single object
          else if (filterConfig.type === 'dropdown' && value && typeof value === 'object' && !Array.isArray(value)) {
            value = value.value;
          }
        }

        if (value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0)) {
          filtersToSend[key] = value;
        }
      }
    }

    if (!filtersToSend['statuses']) {
      filtersToSend['statuses'] = 'pending';
    }
    
    // Update columns based on the status filter
    this.updateColumnsBasedOnStatus(filtersToSend['statuses']);

      const isSearchOperation = currentEvent.globalFilter && currentEvent.globalFilter.trim() !== '';

       if (isSearchOperation) {
    filtersToSend['search'] = currentEvent.globalFilter.trim();
  }


  const context = new HttpContext().set(SHOW_LOADER, !isSearchOperation);


    this.apiService.getSignupRequests(
      this.requestPage,
      this.requestPageSize,
      filtersToSend,
      context
    ).subscribe({
      next: (response) => {
        this.requestData = response.signup_requests.map((req, index) => {
          const assignedIds: number[] = ((req as any).assigned_country_ids as number[]) || [];
          const assignedNames: string[] = assignedIds
            .map((id: number) => this.getCountryName(id))
            .filter((n: string) => !!n && n !== 'N/A');
          const firstName: string = assignedNames[0] || 'N/A';
          const remainingCount: number = Math.max(0, assignedNames.length - 1);
          const remainingNames: string[] = remainingCount > 0 ? assignedNames.slice(1) : [];
          const certificateMeta = this.buildCertificateMeta(req.certificate_info, req.certificate_url);

          return ({
            s_no: (this.requestPage - 1) * this.requestPageSize + index + 1,
            ...req,
            certificate_meta: certificateMeta,
            certificate_url: certificateMeta?.url || null,
            role_name: this.getRoleName(req.role_id),
            country_name: this.getCountryName(req.country_id),
            region_name: this.getRegionName(req.assigned_region_id),
            user_responsible_country_name: firstName,
            resp_first: firstName,
            resp_remaining: remainingCount,
            resp_remaining_names: remainingNames,
            formatted_requested_at: this.formatDate(req.requested_at)
          });
        });
        this.requestTotalRecords = response.meta.total;
        this.totalRequestsChanged.emit(this.requestTotalRecords);
        this.requestLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Failed to load signup requests:', error);
        this.requestData = [];
        this.requestTotalRecords = 0;
        this.totalRequestsChanged.emit(0);
        this.requestLoading = false;
      }
    });
  }

  acceptRequest(row: SignupRequest): void {
    const payload = {
      action: "approved",
      admin_remark: "User credentials verified"
    };

    this.apiService.updateSignupRequestStatus(row.id, payload).subscribe({
      next: (response) => {
        this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "User request has been accepted successfully.",
            life: 3000,
        });
        this.showGenericSuccessDialog = true;
        this.loadSignupRequests(this.requestLastLazyLoadEvent!);
      },
      error: (error: HttpErrorResponse) => {
        console.error("Error approving request:", error);
        let errorMessage = "Failed to accept the request. Please try again.";
        if (error.error?.detail) {
          errorMessage = error.error.detail;
        }
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail: errorMessage,
          life: 5000,
        });
      }
    });
  }

  declineRequest(row: SignupRequest): void {
    this.selectedRequestForDecline = row;
    this.showDeclineRequestDialog = true;
  }

  onDeclineConfirmed(remark: string): void {
    if (this.selectedRequestForDecline) {
      const payload = {
        action: "rejected",
        admin_remark: remark
      };

      this.apiService.updateSignupRequestStatus(this.selectedRequestForDecline.id, payload).subscribe({
        next: (response) => {
          this.messageService.add({
              severity: "success",
              summary: "Success",
              detail: "User request has been declined successfully.",
              life: 3000,
          });
          this.loadSignupRequests(this.requestLastLazyLoadEvent!);
          this.selectedRequestForDecline = null;
        },
        error: (error: HttpErrorResponse) => {
          console.error("Error declining request:", error);
          let errorMessage = "Failed to decline the request. Please try again.";
          if (error.error?.detail) {
            errorMessage = error.error.detail;
          }
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: errorMessage,
            life: 5000,
          });
        }
      });
    }
  }

  onDeclineDialogClose(): void {
    this.showDeclineRequestDialog = false;
    this.selectedRequestForDecline = null;
  }

  onGenericSuccessDialogClose(): void {
    this.showGenericSuccessDialog = false;
  }

  onViewCertificate(event: { url: string, fileName: string }): void {
    
    const isAttachment = event.url.includes('/attachments/download/');
    const proceedPreview = (url: string) => {
      this.pdfUrl = url;
      try {
        this.pdfFileName = decodeURIComponent(event.fileName);
      } catch {
        this.pdfFileName = event.fileName;
      }
      this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      this.displayPdfViewer = true;
      this.cdr.detectChanges();
    };

    if (!isAttachment) {
      proceedPreview(event.url);
      return;
    }

    this.apiService.getSignedAttachmentDownloadFromUrl(event.url).subscribe({
      next: (res) => {
        proceedPreview(res.download_url);
      },
      error: (err) => {
        console.error('Failed to get signed certificate URL', err);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Unable to fetch certificate link. Please try again.', life: 4000 });
      }
    });
  }

  openListPopup(payload: { title: string; items: string[]; x?: number; y?: number }): void {
    this.listPopupTitle = payload.title;
    this.listPopupItems = payload.items;
    this.listPopupX = Math.max(12, Math.min((payload.x ?? 0), window.innerWidth - 280));
    this.listPopupY = Math.max(12, Math.min((payload.y ?? 0), window.innerHeight - 280));
    this.listPopupVisible = true;
  }

  closeListPopup(): void { this.listPopupVisible = false; }

 @HostListener("document:click", ["$event"])
  onDocumentClick(ev: MouseEvent): void {
    const target = ev.target as EventTarget | null;
    const badgeEl = target && (target as Element).closest ? (target as Element).closest(".more-badge") : null;
    if (badgeEl instanceof HTMLElement) {
      const container = badgeEl.closest(".cell-multi");
      const hidden = container?.querySelector(".more-items");
      let list: string[] = [];
      const rawText = hidden?.textContent?.trim();
      if (rawText) {
        try {
          list = JSON.parse(rawText);
        } catch {}
      }
      // if (!list.length) return;
      const titleEl = badgeEl.getAttribute("data-title");
      const title = titleEl || "Countries";
      const rect = badgeEl.getBoundingClientRect();
      this.openListPopup({ title, items: list, x: rect.left, y: rect.bottom });
      return;
    }

    // Close when clicking outside the popup
    const popup = document.querySelector(".list-popup");
    if (popup && target instanceof Node && !popup.contains(target)) {
      this.closeListPopup();
    }
  }

  escapeHtml(unsafe: string) {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }
}